import React, { useMemo } from 'react';

import { IconButton, Tooltip, Typography } from '@material-ui/core';
import CancelIcon from '@material-ui/icons/Cancel';
import CheckCircleIcon from '@material-ui/icons/CheckCircle';
import DeleteOutlinedIcon from '@material-ui/icons/DeleteOutlined';
import EditOutlinedIcon from '@material-ui/icons/EditOutlined';
import ErrorIcon from '@material-ui/icons/Error';
import InfoIcon from '@material-ui/icons/Info';
import NotificationsIcon from '@material-ui/icons/Notifications';
import RemoveCircleOutlineIcon from '@material-ui/icons/RemoveCircleOutline';
import WarningIcon from '@material-ui/icons/Warning';
import { format, isPast } from 'date-fns';
import { useTranslation } from 'react-i18next';
import { useMutation, useQueryClient } from 'react-query';
import { generatePath, useHistory } from 'react-router-dom';

import { ReactComponent as MoreIcon } from 'assets/icons/more.svg';
import ContextMenu from 'components/ContextMenu/ContextMenu';
import Table from 'components/Table';
import actionLibraryInstancesApi from 'config/api/actionLibraryInstance/actionLibraryInstance';
import { ActionLibraryInstance } from 'config/api/actionLibraryInstance/actionLibraryInstance.types';
import ROLES from 'config/constants/ROLES';
import useIsOwner from 'hooks/useIsOwner/useIsOwner';
import usePermissions from 'hooks/usePermissions';
import action_library_messages from 'messages/action_library_messages';
import assets_messages from 'messages/assets_messages';
import { useConfirmationModalContext } from 'reactContext/ConfirmationModalContext/ConfirmationModalContext';
import PATHS from 'router/PATHS';
import { TableDataMappingRow } from 'types/Table';

import useStyles from './ActionLibraryInstancesTable.styles';

type Props = {
  data: ActionLibraryInstance[] | null;
  loading: boolean;
  sort: { key: string | null; direction: string | null };
  onSort: (key: string, direction: string) => void;
  filtersService?: any;
  onSearch?: (searchTerm: string) => void;
  searchValue?: string;
};

const InstanceActionsCell = ({ instance }: { instance: ActionLibraryInstance }) => {
  const { t } = useTranslation();
  const history = useHistory();
  const queryClient = useQueryClient();
  const { checkIsOwner } = useIsOwner();
  const { showConfirmationModal } = useConfirmationModalContext();
  const [isOrgAssetAdmin] = usePermissions([ROLES.ORGANIZATION.ASSETS_MANAGER]);

  const isOwner = checkIsOwner(instance.owner);
  const canDelete = isOrgAssetAdmin || isOwner;

  const { mutate: deleteInstance } = useMutation(actionLibraryInstancesApi.deleteActionLibraryInstance, {
    onSuccess: () => {
      queryClient.invalidateQueries('ACTION_LIBRARY_INSTANCES_GET_ALL');
    },
  });

  const handleEdit = () => {
    history.push(generatePath(PATHS.ACTION_LIBRARY_INSTANCE_DETAIL, { id: instance.id }));
  };

  const handleDelete = async () => {
    // @ts-ignore
    const title = t(action_library_messages.delete_confirmation.title);
    // @ts-ignore
    const body = t(action_library_messages.delete_confirmation.body);

    const confirmed = await showConfirmationModal({
      title,
      body,
    });

    if (confirmed) {
      deleteInstance(instance.id);
    }
  };

  const menuElements = [
    {
      label: t(action_library_messages.actions.edit),
      onClick: handleEdit,
      icon: <EditOutlinedIcon fontSize='small' />,
    },
    ...(canDelete
      ? [
          {
            label: t(action_library_messages.actions.delete),
            onClick: handleDelete,
            icon: <DeleteOutlinedIcon fontSize='small' />,
          },
        ]
      : []),
  ];

  return (
    <ContextMenu elements={menuElements} id={`action-instance-${instance.id}`}>
      <IconButton size='small'>
        <MoreIcon />
      </IconButton>
    </ContextMenu>
  );
};

const ActionLibraryInstancesTable: React.FC<Props> = ({ data, loading, sort, onSort, filtersService, onSearch, searchValue }) => {
  const { t } = useTranslation();
  const styles = useStyles();

  const parsedData = useMemo(() => {
    if (!data) return null;
    return data;
  }, [data]);

  const getRowHref = (instance: ActionLibraryInstance) => generatePath(PATHS.ACTION_LIBRARY_INSTANCE_DETAIL, { id: instance.id });

  const dataMapping: TableDataMappingRow<ActionLibraryInstance>[] = useMemo(
    () => [
      {
        id: 'title',
        width: '20%',
        label: t(action_library_messages.table.name),
        get: (instance: ActionLibraryInstance) => instance.title,
      },
      {
        id: 'asset',
        width: '15%',
        label: t(assets_messages.information_label.name), // "Asset name"
        get: (instance: ActionLibraryInstance) => (
          <div style={{ display: 'flex', flexDirection: 'column' }}>
            <Typography style={{ fontWeight: 'bold' }} variant='body2'>
              {instance.assetName}
            </Typography>
            <Typography color='textSecondary' variant='caption'>
              {instance.assetType}
            </Typography>
          </div>
        ),
      },
      {
        id: 'assetImportance',
        width: '10%',
        label: t(assets_messages.information_label.assets_importance),
        get: (instance: ActionLibraryInstance) => {
          if (!instance.assetImportance) return '-';
          // @ts-ignore
          return t(assets_messages.information_choices_assets_importance[instance.assetImportance]) || instance.assetImportance;
        },
      },
      {
        id: 'criticality',
        width: '10%',
        label: t(action_library_messages.table.criticality),
        get: (instance: ActionLibraryInstance) => {
          const { criticality } = instance;
          const criticalityKey = criticality.toUpperCase() as 'LOW' | 'MEDIUM' | 'HIGH';
          let IconComponent;
          let iconClass;

          if (criticality === 'low') {
            IconComponent = InfoIcon;
            iconClass = styles.criticalityLowIcon;
          } else if (criticality === 'medium') {
            IconComponent = WarningIcon;
            iconClass = styles.criticalityMediumIcon;
          } else {
            IconComponent = ErrorIcon;
            iconClass = styles.criticalityHighIcon;
          }

          return (
            <div className={styles.statusCell}>
              <IconComponent className={`${styles.statusIcon} ${iconClass}`} />
              <span>{t(action_library_messages.criticality[criticalityKey])}</span>
            </div>
          );
        },
      },
      {
        id: 'deadline',
        width: '15%',
        label: t(action_library_messages.instance.form.deadline),
        get: (instance: ActionLibraryInstance) => {
          const deadlineDate = instance.deadline ? new Date(instance.deadline) : null;
          const reminderDate = instance.reminder ? new Date(instance.reminder) : null;

          const isDeadlinePast = deadlineDate && isPast(deadlineDate) && instance.status !== 'completed';
          const isReminderPast = reminderDate && isPast(reminderDate) && instance.status !== 'completed';

          return (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
              <div style={{ display: 'flex', alignItems: 'center' }}>
                {deadlineDate ? (
                  <Typography
                    style={{
                      color: isDeadlinePast ? '#f44336' : 'inherit', // Red if past
                      fontWeight: isDeadlinePast ? 'bold' : 'normal',
                      marginRight: reminderDate ? '8px' : '0',
                    }}
                    variant='body2'
                  >
                    {format(deadlineDate, 'yyyy-MM-dd')}
                  </Typography>
                ) : (
                  <Typography style={{ marginRight: reminderDate ? '8px' : '0' }} variant='body2'>
                    -
                  </Typography>
                )}
                {reminderDate && (
                  <Tooltip title={`${t(action_library_messages.instance.form.reminder)}: ${format(reminderDate, 'yyyy-MM-dd')}`}>
                    <NotificationsIcon
                      style={{
                        fontSize: '1.1rem',
                        color: isReminderPast ? '#ff9800' : 'rgba(0, 0, 0, 0.54)',
                      }}
                    />
                  </Tooltip>
                )}
              </div>
            </div>
          );
        },
      },
      {
        id: 'status',
        width: '10%',
        label: t(action_library_messages.table.status),
        get: (instance: ActionLibraryInstance) => {
          let IconComponent;
          let iconClass;
          const { status } = instance;

          if (status === 'completed') {
            IconComponent = CheckCircleIcon;
            iconClass = styles.activeIcon;
          } else if (status === 'in_progress') {
            IconComponent = RemoveCircleOutlineIcon;
            iconClass = styles.criticalityMediumIcon;
          } else {
            IconComponent = CancelIcon;
            iconClass = styles.inactiveIcon;
          }

          return (
            <div className={styles.statusCell}>
              <IconComponent className={`${styles.statusIcon} ${iconClass}`} />
              <span>{status}</span>
            </div>
          );
        },
      },
      {
        id: 'owner',
        width: '15%',
        label: 'Owner', // Should use translation if available
        get: (instance: ActionLibraryInstance) => instance.ownerFullName,
      },
      {
        id: 'actions',
        width: '5%',
        label: '',
        get: (instance: ActionLibraryInstance) => <InstanceActionsCell instance={instance} />,
      },
    ],
    [t, styles],
  );

  return (
    <>
      <Table
        clickableRow
        data={parsedData}
        dataMapping={dataMapping}
        enableSearch={!!onSearch}
        filtersService={filtersService}
        getRowHref={getRowHref}
        onSearch={onSearch}
        onSort={onSort}
        refetching={loading}
        searchValue={searchValue}
        sort={sort}
      />
    </>
  );
};

export default ActionLibraryInstancesTable;
